import { useMemo, useRef, useState, type PointerEvent } from 'react';
import { legalMovesFor, pieceNames, type Piece, type PieceType, type Side, type Square } from '../lib/janggi';

type Props = {
  pieces: Piece[];
  perspective?: Side;
  interactiveSide?: Side | 'both';
  allowedMoves?: Array<{ from: Square; to: Square }>;
  guides?: Array<{ square: Square; label?: string; tone: 'origin' | 'target' }>;
  disabled?: boolean;
  onMove?: (piece: Piece, destination: Square) => void;
  compact?: boolean;
  arrows?: Array<{ from: Square; to: Square; tone?: 'best' | 'alternative' }>;
  lastMove?: { from: Square; to: Square } | null;
  checkSide?: Side | null;
  showCoordinates?: boolean;
};

const key = (file: number, rank: number) => `${file}:${rank}`;
const assetNames: Record<PieceType, string> = {
  king: 'king',
  guard: 'advisor',
  chariot: 'chariot',
  cannon: 'cannon',
  horse: 'horse',
  elephant: 'elephant',
  soldier: 'pawn',
};
const PIECE_ASSET_VERSION = '2026-07-16-glyph-6';
const pieceAsset = (piece: Piece) => `/pieces/classic/${piece.side === 'cho' ? 'blue' : 'red'}_${assetNames[piece.type]}.svg?v=${PIECE_ASSET_VERSION}`;
const pieceSize = (type: PieceType): 'large' | 'medium' | 'small' => {
  if (type === 'king') return 'large';
  if (type === 'guard' || type === 'soldier') return 'small';
  return 'medium';
};

const startMarkers = [
  [1, 2], [7, 2], [0, 3], [2, 3], [4, 3], [6, 3], [8, 3],
  [0, 6], [2, 6], [4, 6], [6, 6], [8, 6], [1, 7], [7, 7],
];

export function GameBoard({ pieces, perspective = 'cho', interactiveSide = 'both', allowedMoves, guides, disabled, onMove, compact, arrows = [], lastMove, checkSide, showCoordinates = false }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ pieceId: string; startX: number; startY: number; x: number; y: number; moved: boolean } | null>(null);
  const dragRef = useRef<{ pieceId: string; pointerId: number; startX: number; startY: number; x: number; y: number; moved: boolean } | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const ignoreClick = useRef(false);
  const selected = pieces.find((piece) => piece.id === selectedId) ?? null;
  const moves = useMemo(() => {
    if (!selected) return [];
    if (allowedMoves) return allowedMoves
      .filter((move) => move.from.file === selected.file && move.from.rank === selected.rank)
      .map((move) => move.to);
    return legalMovesFor(selected, pieces);
  }, [selected, pieces, allowedMoves]);
  const moveKeys = new Set(moves.map((move) => key(move.file, move.rank)));
  const files = perspective === 'cho' ? [...Array(9).keys()] : [...Array(9).keys()].reverse();
  const ranks = perspective === 'cho' ? [...Array(10).keys()] : [...Array(10).keys()].reverse();
  const destinationsFor = (piece: Piece) => allowedMoves
    ? allowedMoves.filter((move) => move.from.file === piece.file && move.from.rank === piece.rank).map((move) => move.to)
    : legalMovesFor(piece, pieces);

  const choose = (file: number, rank: number) => {
    if (ignoreClick.current) { ignoreClick.current = false; return; }
    if (disabled) return;
    const target = pieces.find((piece) => piece.file === file && piece.rank === rank);
    if (selected && moveKeys.has(key(file, rank))) {
      onMove?.(selected, { file, rank });
      setSelectedId(null);
      return;
    }
    if (target?.id === selectedId) { setSelectedId(null); return; }
    const canSelect = target && (interactiveSide === 'both' || target.side === interactiveSide);
    setSelectedId(canSelect ? target.id : null);
  };

  const beginDrag = (event: PointerEvent<HTMLButtonElement>, file: number, rank: number) => {
    if (disabled || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const piece = pieces.find((item) => item.file === file && item.rank === rank);
    if (!piece || (interactiveSide !== 'both' && piece.side !== interactiveSide)) return;
    event.preventDefault();
    boardRef.current?.setPointerCapture(event.pointerId);
    const next = { pieceId: piece.id, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, x: event.clientX, y: event.clientY, moved: false };
    dragRef.current = next;
    setDrag(next);
  };

  const trackDrag = (event: PointerEvent<HTMLDivElement>) => {
    const current = dragRef.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const moved = current.moved || Math.hypot(event.clientX - current.startX, event.clientY - current.startY) > 3;
    if (moved && !current.moved) setSelectedId(current.pieceId);
    const next = { ...current, x: event.clientX, y: event.clientY, moved };
    dragRef.current = next;
    setDrag(next);
  };

  const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
    const current = dragRef.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const moved = current.moved || Math.hypot(event.clientX - current.startX, event.clientY - current.startY) > 3;
    const piece = pieces.find((item) => item.id === current.pieceId);
    const board = boardRef.current;
    const rect = board?.getBoundingClientRect();
    if (moved && piece && board && rect) {
      const boardLeft = rect.left + board.clientLeft;
      const boardTop = rect.top + board.clientTop;
      const column = Math.floor((event.clientX - boardLeft) / (board.clientWidth / 9));
      const row = Math.floor((event.clientY - boardTop) / (board.clientHeight / 10));
      const inside = column >= 0 && column < 9 && row >= 0 && row < 10;
      if (inside) {
        const file = perspective === 'cho' ? column : 8 - column;
        const rank = perspective === 'cho' ? row : 9 - row;
        const legal = destinationsFor(piece).some((destination) => destination.file === file && destination.rank === rank);
        if (legal) onMove?.(piece, { file, rank });
      }
      setSelectedId(null);
      ignoreClick.current = true;
      window.setTimeout(() => { ignoreClick.current = false; }, 0);
    }
    dragRef.current = null;
    setDrag(null);
    if (boardRef.current?.hasPointerCapture(event.pointerId)) boardRef.current.releasePointerCapture(event.pointerId);
  };

  const cancelDrag = () => {
    dragRef.current = null;
    setDrag(null);
    setSelectedId(null);
  };

  return (
    <div ref={boardRef} className={`game-board${compact ? ' game-board--compact' : ''}${drag?.moved ? ' game-board--dragging' : ''}`} role="grid" aria-label="장기판" onPointerMove={trackDrag} onPointerUp={finishDrag} onPointerCancel={cancelDrag}>
      <svg className="game-board__lines" viewBox="0 0 800 900" aria-hidden="true">
        <rect className="game-board__frame" x="3" y="3" width="794" height="894" />
        {Array.from({ length: 7 }, (_, index) => <line key={`v-${index + 1}`} x1={(index + 1) * 100} y1="3" x2={(index + 1) * 100} y2="897" />)}
        {Array.from({ length: 8 }, (_, index) => <line key={`h-${index + 1}`} x1="3" y1={(index + 1) * 100} x2="797" y2={(index + 1) * 100} />)}
        <path d="M300 0 500 200M500 0 300 200M300 700 500 900M500 700 300 900" />
      </svg>
      {arrows.length > 0 && <svg className="game-board__arrows" viewBox="0 0 800 900" aria-label="엔진 추천 수"><defs><marker id="game-arrow-best" markerWidth="7" markerHeight="7" refX="5.5" refY="3.5" orient="auto"><path className="game-arrow-head--best" d="M0 0 L7 3.5 L0 7 Z" /></marker><marker id="game-arrow-alt" markerWidth="7" markerHeight="7" refX="5.5" refY="3.5" orient="auto"><path className="game-arrow-head--alternative" d="M0 0 L7 3.5 L0 7 Z" /></marker></defs>{arrows.map((arrow, index) => { const x1 = (perspective === 'cho' ? arrow.from.file : 8 - arrow.from.file) * 100; const y1 = (perspective === 'cho' ? arrow.from.rank : 9 - arrow.from.rank) * 100; const x2 = (perspective === 'cho' ? arrow.to.file : 8 - arrow.to.file) * 100; const y2 = (perspective === 'cho' ? arrow.to.rank : 9 - arrow.to.rank) * 100; return <line key={`${arrow.from.file}:${arrow.from.rank}-${arrow.to.file}:${arrow.to.rank}-${index}`} className={`game-board__arrow game-board__arrow--${arrow.tone || 'alternative'}`} x1={x1} y1={y1} x2={x2} y2={y2} markerEnd={`url(#game-arrow-${arrow.tone === 'best' ? 'best' : 'alt'})`} />; })}</svg>}
      <svg className="game-board__markers" viewBox="0 0 800 900" aria-hidden="true">
        {startMarkers.map(([file, rank]) => <path key={`${file}-${rank}`} d={`M${file * 100 - 13} ${rank * 100 - 13} L${file * 100 + 13} ${rank * 100 + 13} M${file * 100 + 13} ${rank * 100 - 13} L${file * 100 - 13} ${rank * 100 + 13}`} />)}
      </svg>
      {ranks.flatMap((rank) => files.map((file) => {
        const piece = pieces.find((item) => item.file === file && item.rank === rank);
        const move = moveKeys.has(key(file, rank));
        const guide = guides?.find((item) => item.square.file === file && item.square.rank === rank);
        const lifted = piece && (piece.id === selectedId || piece.id === drag?.pieceId);
        const isLastFrom = lastMove?.from.file === file && lastMove?.from.rank === rank;
        const isLastTo = lastMove?.to.file === file && lastMove?.to.rank === rank;
        const isCheckedKing = Boolean(piece && piece.type === 'king' && piece.side === checkSide);
        const isCapture = Boolean(move && piece && selected && piece.side !== selected.side);
        return (
          <button
            type="button"
            role="gridcell"
            className={`game-square${piece?.id === selectedId ? ' game-square--selected' : ''}${lifted ? ' game-square--lifted' : ''}${move ? ' game-square--move' : ''}${isLastFrom ? ' game-square--last-from' : ''}${isLastTo ? ' game-square--last-to' : ''}${isCheckedKing ? ' game-square--check' : ''}`}
            key={key(file, rank)}
            data-board-square
            data-file={file}
            data-rank={rank}
            aria-label={`${piece ? `${piece.side === 'cho' ? '초' : '한'} ${pieceNames[piece.type]} ` : ''}${file + 1}선 ${rank + 1}단${move ? ' 이동 가능' : ''}${guide ? `, ${guide.label || '안내 위치'}` : ''}`}
            onClick={() => choose(file, rank)}
            onPointerDown={(event) => beginDrag(event, file, rank)}
          >
            {guide && <span className={`game-square-guide game-square-guide--${guide.tone}`}>{guide.label && <b>{guide.label}</b>}</span>}
            {move && <span className={`game-move-dot${isCapture ? ' game-move-dot--capture' : ''}`} />}
            {showCoordinates && rank === (perspective === 'cho' ? 9 : 0) && <span className="game-coordinate game-coordinate--file">{file + 1}</span>}
            {showCoordinates && file === (perspective === 'cho' ? 0 : 8) && <span className="game-coordinate game-coordinate--rank">{rank + 1}</span>}
            {piece && (
              <img
                className={`game-piece game-piece--${piece.side} game-piece--${piece.type} game-piece--size-${pieceSize(piece.type)}`}
                src={pieceAsset(piece)}
                alt=""
                draggable={false}
                style={drag?.pieceId === piece.id && drag.moved ? {
                  transform: `translate3d(${drag.x - drag.startX}px, ${drag.y - drag.startY}px, 0) scale(1.08)`,
                  zIndex: 10,
                } : undefined}
              />
            )}
          </button>
        );
      }))}
    </div>
  );
}
